import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Group, Progress, Stack, Text } from '@mantine/core';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Circle,
  CircleDot,
  FlaskConical,
  HelpCircle,
  Play,
} from 'lucide-react';
import { useMemo } from 'react';
import { Badge, Button, Card, PageContainer, PageHeader, Section, Skeleton } from '../components/ui';
import { MarkdownBody } from '../components/lessons/LessonView';
import { useCourse } from '../hooks/useCourses';
import { useLab } from '../hooks/useLabs';
import { useLabProgress } from '../hooks/useLabProgress';
import type { CourseModule, CourseTopic } from '../api/types';

export const Route = createFileRoute('/courses/$id/topic/$topic')({
  component: TopicPage,
});

/**
 * Full-page view of a single course topic: the lesson (theory) up top, then the
 * lab that ends the topic with its actual questions and the learner's progress,
 * and prev/next navigation through the course. Replaces the old accordion where
 * every topic was crammed into a dropdown.
 */
function TopicPage() {
  const { id, topic: topicSlug } = Route.useParams();
  const navigate = useNavigate();
  const courseQ = useCourse(id);

  const flat = useMemo(() => {
    const out: Array<{ module: CourseModule; topic: CourseTopic; nAcross: number }> = [];
    let n = 0;
    for (const m of courseQ.data?.modules ?? []) {
      for (const t of m.topics) {
        n += 1;
        out.push({ module: m, topic: t, nAcross: n });
      }
    }
    return out;
  }, [courseQ.data]);

  const pos = flat.findIndex((x) => x.topic.slug === topicSlug);
  const cur = pos >= 0 ? flat[pos] : undefined;
  const prev = pos > 0 ? flat[pos - 1] : undefined;
  const next = pos >= 0 && pos < flat.length - 1 ? flat[pos + 1] : undefined;

  const labId = cur?.topic.labId ?? undefined;
  const labQ = useLab(labId);
  const progQ = useLabProgress(labId);

  if (courseQ.isPending) {
    return (
      <PageContainer size="default">
        <Stack gap="lg">
          <Skeleton height={40} width="55%" />
          <Skeleton height={320} radius="md" />
        </Stack>
      </PageContainer>
    );
  }

  if (courseQ.isError || !courseQ.data || !cur) {
    return (
      <PageContainer size="default">
        <Alert color="red" icon={<AlertTriangle size={16} />} variant="light" title="Topic not found">
          This topic isn&apos;t part of the course.{' '}
          <Button variant="ghost" size="xs" onClick={() => navigate({ to: '/courses/$id', params: { id } })}>
            Back to course
          </Button>
        </Alert>
      </PageContainer>
    );
  }

  const course = courseQ.data;
  const status = progQ.data?.status ?? 'not_started';
  const pct = progQ.data?.percentComplete ?? 0;
  const completed = status === 'completed' || pct >= 100;
  const inProgress = !completed && (pct > 0 || ['ready', 'running', 'attacking', 'provisioning', 'pending'].includes(status));

  return (
    <PageContainer size="default">
      <PageHeader
        icon={BookOpen}
        title={cur.topic.title}
        description={`${course.title} · ${cur.module.title}`}
        breadcrumbs={[
          { label: 'Forge', to: '/' },
          { label: 'Courses', to: '/courses' },
          { label: course.title, to: `/courses/${id}` },
          { label: `Topic ${cur.nAcross}` },
        ]}
        actions={
          <Badge variant={completed ? 'success' : inProgress ? 'info' : 'neutral'}>
            {completed ? 'Completed' : inProgress ? 'In progress' : 'Not started'}
          </Badge>
        }
      />

      {/* The lesson — full width, no dropdown. */}
      <Card style={{ marginBottom: 20 }}>
        {cur.topic.body ? (
          <MarkdownBody source={cur.topic.body} />
        ) : (
          <Text c="dimmed" size="sm">This topic has no written lesson yet.</Text>
        )}
      </Card>

      {/* The lab that ends the topic — its real questions + the learner's progress. */}
      {labId ? (
        <Section
          title="Your lab"
          description="Apply what you just read. Launch the lab and answer these questions to complete the topic."
        >
          <Card variant="elevated">
            <Stack gap="md">
              <Group justify="space-between" align="center" wrap="wrap" gap="sm">
                <Group gap="sm" wrap="nowrap">
                  <FlaskConical size={18} color="rgb(var(--c-neon))" />
                  <Stack gap={2}>
                    <Text fw={700} size="sm" c="rgb(var(--c-txt-bright))">
                      {labQ.data?.title ?? cur.topic.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {completed
                        ? 'You completed this lab.'
                        : inProgress
                          ? `In progress — ${pct}% complete`
                          : 'Not started yet'}
                    </Text>
                  </Stack>
                </Group>
                <Button
                  variant="primary"
                  leftIcon={completed ? <CheckCircle2 size={14} /> : <Play size={14} />}
                  onClick={() => navigate({ to: '/labs/$id', params: { id: labId } })}
                >
                  {completed ? 'Review lab' : inProgress ? 'Resume lab' : 'Start lab'}
                </Button>
              </Group>

              {pct > 0 ? <Progress value={pct} size="sm" color="teal" radius="xl" /> : null}

              {/* The questions the lab asks. */}
              <Stack gap={2}>
                <Group gap={6} mb={4}>
                  <HelpCircle size={14} color="rgb(var(--c-txt-muted))" />
                  <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                    Questions you&apos;ll answer
                  </Text>
                </Group>
                {labQ.isPending ? (
                  <Skeleton lines={3} height={40} gap={8} />
                ) : (labQ.data?.tasks?.length ?? 0) === 0 ? (
                  <Text size="sm" c="dimmed">Launch the lab to see its tasks.</Text>
                ) : (
                  <Stack gap={8}>
                    {labQ.data!.tasks.map((task, i) => {
                      const tp = progQ.data?.tasks?.find((x) => String(x.taskId) === String(i));
                      const solved = tp?.status === 'completed';
                      const attempted = tp?.status === 'failed' || (tp?.attempts ?? 0) > 0;
                      return (
                        <Card key={task.id} style={{ padding: 12 }}>
                          <Group gap="sm" wrap="nowrap" align="flex-start">
                            <span style={{ marginTop: 2, flexShrink: 0 }}>
                              {solved ? (
                                <CheckCircle2 size={16} color="rgb(var(--c-neon))" />
                              ) : attempted ? (
                                <CircleDot size={16} color="#fbbf24" />
                              ) : (
                                <Circle size={16} color="rgb(var(--c-txt-muted))" />
                              )}
                            </span>
                            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                              <Group gap={8} wrap="nowrap">
                                <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">
                                  {i + 1}. {task.title}
                                </Text>
                                {task.points ? (
                                  <Badge variant="neutral" size="xs">{task.points} pts</Badge>
                                ) : null}
                              </Group>
                              {task.description ? (
                                <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                                  {task.description}
                                </Text>
                              ) : null}
                            </Stack>
                          </Group>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Card>
        </Section>
      ) : null}

      {/* Prev / next topic navigation. */}
      <Group justify="space-between" mt="xl" wrap="nowrap">
        {prev ? (
          <Button
            variant="ghost"
            leftIcon={<ArrowLeft size={14} />}
            onClick={() => navigate({ to: '/courses/$id/topic/$topic', params: { id, topic: prev.topic.slug } })}
          >
            <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {prev.topic.title}
            </span>
          </Button>
        ) : (
          <span />
        )}
        {next ? (
          <Button
            variant="primary"
            rightIcon={<ArrowRight size={14} />}
            onClick={() => navigate({ to: '/courses/$id/topic/$topic', params: { id, topic: next.topic.slug } })}
          >
            <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Next: {next.topic.title}
            </span>
          </Button>
        ) : (
          <Button
            variant="secondary"
            rightIcon={<ArrowRight size={14} />}
            onClick={() => navigate({ to: '/courses/$id', params: { id } })}
          >
            Back to course
          </Button>
        )}
      </Group>
    </PageContainer>
  );
}
